import { FC, useState, useEffect } from 'react';
import { DirectVideoPlayer } from './DirectVideoPlayer';
import { YouTubePlayer } from './YouTubePlayer';
import { Play } from 'lucide-react';
import { useLessonProgress } from '@/hooks/useLessonProgress';

interface VideoPlayerProps {
  url: string;
  lessonId: string;
  onVideoComplete?: () => void;
  onVideoProgress?: (progress: number) => void;
}

export const VideoPlayer: FC<VideoPlayerProps> = ({
  url,
  lessonId,
  onVideoComplete,
  onVideoProgress
}) => {
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const { updateProgress } = useLessonProgress(lessonId);

  const handleVideoComplete = async () => {
    if (completed) return;
    
    try {
      await updateProgress.mutateAsync({
        type: 'video',
        completed: true,
        completed_at: new Date().toISOString()
      });

      setCompleted(true);
      onVideoComplete?.();
    } catch (error) {
      console.error('Error al guardar progreso del video:', error);
    }
  };

  const handleProgressUpdate = (newProgress: number) => {
    setProgress(newProgress);
    onVideoProgress?.(newProgress);
    if (newProgress >= 90 && !completed) {
      handleVideoComplete();
    }
  };

  if (!url) {
    return (
      <div className="relative aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="bg-black/40 backdrop-blur-sm p-3 rounded-full">
          <Play className="w-8 h-8 text-gray-400" />
        </div>
        <p className="absolute bottom-4 text-gray-500 text-sm">Video no disponible</p>
      </div>
    );
  }

  const isYouTubeVideo = url.includes('youtube.com') || url.includes('youtu.be');

  return isYouTubeVideo ? (
    <YouTubePlayer
      videoUrl={url}
      onVideoComplete={handleVideoComplete}
      onProgressUpdate={handleProgressUpdate}
    />
  ) : (
    <DirectVideoPlayer
      videoUrl={url}
      onVideoComplete={handleVideoComplete}
      onProgressUpdate={handleProgressUpdate}
    />
  );
};